import { useMemo, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { formatDateOrEmpty, isUnsetDate } from '@helpers/dates';
import uniqBy from 'lodash.uniqby';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Typography } from '@components/Typography';
import Autocomplete from '@components/Autocomplete';
import { Input, PrefixedInput } from '@components/Input';
import { Select } from '@components/Select';
import PhoneInput from '@components/PhoneInput';
import TimeframePicker from '@components/TimeframePicker';
import { Checkbox } from '@components/Checkbox';
import { MultiSelectLabels } from '@components/MultiSelectLabels';
import { alertActions, contactsActions } from '@actions';
import { contactsService } from '@services';
import { handleApiError } from '@helpers/api';
import { preparePhoneNumberForSubmit, setInitialPhoneNumberToForm } from '@helpers/phone';
import { ModalType } from '@constants/modal';
import { useTranslation, useYupValidationResolver, useContactLocations, useIntlLocale } from '@hooks';
import { Modal, Content, ActivityWrapper, CheckboxWrapper } from './styles';
import { LabelType } from '@constants/labels';
import {
    EARLIEST_BIRTH_DATE,
    SEX_LABEL_KEYS,
    Sex,
    birthDateForSubmit,
    dateFromInputValue,
    dateToInputValue,
    formatBirthDate,
    isBirthDateShaped,
    languageOptions,
    storedBirthDateWithoutYear,
    toDateInputValue,
} from '@helpers/contact-demographics';

const EditContactModal = ({ modal, onUpdateCallback }) => {
    const { t } = useTranslation();
    const intlLocale = useIntlLocale();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const account = useSelector(state => state.account?.account);
    const { contact } = useSelector(state => state.contacts);
    const { data: labels } = useSelector(state => state.labels);

    const { contactLocations, getLocationsOptions, mapSelectedLocationsToFormValue } = useContactLocations();
    const isMobile = useMediaQuery('@media (max-width: 600px)');
    const dispatch = useDispatch();

    const hasSingleLocation = contactLocations.length === 1;
    const singleLocationId = hasSingleLocation ? contactLocations[0].companyId : null;

    const labelsOptions = useMemo(() => labels.map(item => ({ value: item.id, label: item.name })), [labels]);
    const formInitialized = useRef(false);

    // Fetch fresh on open (not just when empty): the lookup persists across RESET_CONTACTS and can
    // be stale if locations changed during the session.
    useEffect(() => {
        dispatch(contactsActions.getLocations());
    }, [dispatch]);

    const ContactSchema = yup.object().shape(
        {
            email: yup.string().when('email', {
                is: val => !val?.length,
                then: schema => schema.notRequired(),
                otherwise: schema => schema.required('validation.required').email('validation.wrongEmailFormat'),
            }),
            phoneNumber: yup.string().validatePhone('validation.wrongPhoneFormat', true),
            dateOfBirth: yup.string().test('birthDateInRange', 'validation.dateOfBirthOutOfRange', isBirthDateShaped),
            companies: yup.array(),
        },
        [['email', 'email']],
    );

    const resolver = useYupValidationResolver(ContactSchema, () => ({ dirtyFields, touchedFields }));

    const {
        getValues,
        setValue,
        control,
        formState: { isValid, isDirty, dirtyFields, touchedFields },
    } = useForm({
        mode: 'all',
        resolver,
        defaultValues: {
            firstName: '',
            lastName: '',
            phoneNumber: '',
            email: '',
            facebookProfile: '',
            instagramProfile: '',
            companies: [],
            unsubscribed: false,
            labels: [],
            sex: '',
            dateOfBirth: '',
            language: '',
        },
    });

    const locationsOptions = useMemo(() => getLocationsOptions(), [getLocationsOptions]);

    const autoSelectSingleLocation = hasSingleLocation && locationsOptions.some(o => o.id === singleLocationId);

    useEffect(() => {
        if (modal.type === ModalType.Create && autoSelectSingleLocation) {
            setValue('companies', mapSelectedLocationsToFormValue([singleLocationId]), { shouldValidate: true });
        }
    }, [autoSelectSingleLocation, singleLocationId, modal.type, setValue, mapSelectedLocationsToFormValue]);

    useEffect(() => {
        if (formInitialized.current) return;

        if (modal.type === ModalType.Edit && contact) {
            formInitialized.current = true;
            setValue('phoneNumber', setInitialPhoneNumberToForm(contact?.phoneNumber, account?.locale), {
                shouldValidate: true,
                shouldTouch: true,
            });
            setValue('firstName', contact?.firstName || '');
            setValue('lastName', contact?.lastName || '');

            setValue('email', contact?.email || '', { shouldValidate: true });
            setValue('facebookProfile', contact?.facebookProfile || '', { shouldValidate: true });
            setValue('instagramProfile', contact?.instagramProfile || '', { shouldValidate: true });
            setValue('labels', contact?.labels?.map(l => l.id) || [], { shouldValidate: true });
            setValue('unsubscribed', !!contact?.unsubscribed, { shouldValidate: true });
            setValue('sex', contact?.sex ?? '');
            setValue('dateOfBirth', toDateInputValue(contact || {}));
            setValue('language', contact?.language || '');
        } else if (modal.type === ModalType.Create) {
            formInitialized.current = true;
            setValue('phoneNumber', setInitialPhoneNumberToForm('', account?.locale), {
                shouldValidate: false,
                shouldTouch: true,
            });
        }
    }, [contact, modal.type]);

    // Re-map the location chips whenever the lookup updates (e.g. it loaded after the form opened,
    // or refreshed mid-session), but stop once the user has edited the field so we never clobber
    // their changes. mapSelectedLocationsToFormValue keeps unresolved ids, so this never truncates.
    useEffect(() => {
        if (modal.type !== ModalType.Edit || !contact || dirtyFields.companies) return;
        setValue('companies', mapSelectedLocationsToFormValue(contact?.companies), { shouldValidate: true });
    }, [contact, contactLocations, modal.type, dirtyFields.companies, setValue, mapSelectedLocationsToFormValue]);

    const editedContact = modal.type === ModalType.Edit ? contact : null;
    const languages = useMemo(
        () => languageOptions(intlLocale, [editedContact?.language]),
        [intlLocale, editedContact?.language],
    );
    const storedWithoutYear = storedBirthDateWithoutYear(editedContact);
    const today = useMemo(() => new Date(), []);

    const onSaveContact = async () => {
        const values = getValues();
        setIsSubmitting(true);
        let newContact;
        try {
            newContact = await contactsService.createOrUpdateContact({
                ...(modal.type === ModalType.Edit && { id: contact?.id, ...contact }),
                ...values,
                ...birthDateForSubmit(values.dateOfBirth, storedWithoutYear),
                sex: values.sex === '' ? null : values.sex,
                language: values.language || null,
                companies: values.companies.map(c => c.id),
                labels: labels.filter(label => values.labels.includes(label.id)),
                accountId: account?.id,
                phoneNumber: preparePhoneNumberForSubmit(values?.phoneNumber, true),
            });
        } catch (error) {
            handleApiError(error, `alertMessages.${modal.type === ModalType.Create ? 'createFail' : 'editFail'}`);
            return;
        } finally {
            setIsSubmitting(false);
        }

        dispatch(contactsActions.setContact(newContact));
        if (onUpdateCallback) {
            // INFO: callback to update contact during edit or merge
            onUpdateCallback(newContact, newContact?.id);
        }
        dispatch(
            alertActions.success(
                t(`alertMessages.${modal.type === ModalType.Create ? 'createSuccess' : 'editSuccess'}`),
            ),
        );
        onCloseEditModal();
    };

    const onOpenDeleteModal = () => dispatch(contactsActions.setContactDeleteModal(true));

    const onOpenMergeModal = () => dispatch(contactsActions.setContactMergeModal(true));

    const onCloseEditModal = () => {
        formInitialized.current = false;
        dispatch(contactsActions.setContactEditModal({ open: false, type: ModalType.Create }));
    };

    // INFO: update contact labels
    const onUpdateLabels = updatedLabels => {
        const contactLabels = uniqBy([...updatedLabels, ...(contact?.labels || [])], 'id');
        dispatch(contactsActions.setContact({ ...contact, labels: contactLabels }));
    };

    // INFO: delete contact label
    const onDeleteLabel = labelId => {
        const contactLabels = contact?.labels?.filter(l => l.id !== labelId);
        dispatch(contactsActions.setContact({ ...contact, labels: contactLabels }));
    };

    return (
        <Modal
            fullScreen
            isOpen={modal.open}
            handleClose={onCloseEditModal}
            isDirty={isDirty}
            title={modal.type === ModalType.Create ? t('ContactsPage.createTitle') : t('ContactsPage.editTitle')}
            subtitle={
                modal.type === ModalType.Create ? t('ContactsPage.createSubtitle') : t('ContactsPage.editSubtitle')
            }
            onPrimaryAction={onSaveContact}
            primaryActionText={modal.type === ModalType.Create ? t('buttons.create') : t('buttons.save')}
            primaryActionDisabled={isSubmitting || !isValid || !isDirty}
            onAdditionalAction={modal.type === ModalType.Edit ? onOpenDeleteModal : null}
            additionalTextButton={modal.type === ModalType.Edit ? t('buttons.delete') : null}
            onSecondAdditionalAction={modal.type === ModalType.Edit ? onOpenMergeModal : null}
            secondAdditionalTextButton={modal.type === ModalType.Edit ? t('buttons.merge') : null}
        >
            <Content>
                <Controller
                    control={control}
                    name="firstName"
                    render={({ field, fieldState: { error } }) => (
                        <Input
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            label={t('common.firstName')}
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="lastName"
                    render={({ field, fieldState: { error } }) => (
                        <Input
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            label={t('common.lastName')}
                            fullWidth
                            error={!!error}
                            helperText={error ? error.message : null}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="phoneNumber"
                    render={({ field, fieldState: { error } }) => (
                        <PhoneInput
                            placeholder=""
                            label={t('common.phoneNumber')}
                            inputProps={{ name: 'phoneNumber' }}
                            value={field.value}
                            autoFormat
                            onChange={value => {
                                setValue('phoneNumber', value, {
                                    shouldValidate: true,
                                    shouldDirty: true,
                                    shouldTouch: true,
                                });
                            }}
                            error={error ? t(error.message) : null}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="email"
                    render={({ field, fieldState: { error } }) => (
                        <Input
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            label={t('common.email')}
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="sex"
                    render={({ field }) => (
                        <Select
                            {...field}
                            label={t('ContactsPage.sex')}
                            fullWidth
                            options={[
                                { value: '', label: t('common.none') },
                                ...Object.values(Sex).map(value => ({ value, label: t(SEX_LABEL_KEYS[value]) })),
                            ]}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="dateOfBirth"
                    render={({ field, fieldState: { error } }) => (
                        <div>
                            <TimeframePicker
                                value={[dateFromInputValue(field.value)]}
                                range={false}
                                label={t('ContactsPage.dateOfBirth')}
                                onChangeTimeframe={date => field.onChange(dateToInputValue(date))}
                                minDate={EARLIEST_BIRTH_DATE}
                                maxDate={today}
                                clearable
                                error={error}
                            />
                            {!error && !field.value && storedWithoutYear && (
                                <Typography variant="caption" color="textSecondary">
                                    {t('ContactsPage.dateOfBirthWithoutYear', {
                                        date: formatBirthDate(editedContact, intlLocale),
                                    })}
                                </Typography>
                            )}
                        </div>
                    )}
                />
                <Controller
                    control={control}
                    name="language"
                    render={({ field }) => (
                        <Select
                            {...field}
                            label={t('ContactsPage.language')}
                            fullWidth
                            options={[{ value: '', label: t('common.notSpecified') }, ...languages]}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="facebookProfile"
                    render={({ field, fieldState: { error } }) => (
                        <PrefixedInput
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            label={t('ContactsPage.facebookProfile')}
                            fullWidth
                            prefix="facebook.com/"
                            error={!!error}
                            helperText={error ? error.message : null}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="instagramProfile"
                    render={({ field, fieldState: { error } }) => (
                        <PrefixedInput
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            label={t('ContactsPage.instagramProfile')}
                            fullWidth
                            prefix="instagram.com/"
                            error={!!error}
                            helperText={error ? error.message : null}
                        />
                    )}
                />
                {!autoSelectSingleLocation && (
                    <Controller
                        control={control}
                        name="companies"
                        render={({ field, fieldState: { error }, ref }) => (
                            <Autocomplete
                                {...field}
                                ref={ref}
                                label={t('common.locations')}
                                options={locationsOptions}
                                value={field.value}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                inputProps={{ style: { display: field?.value?.length > 0 ? 'none' : 'block' } }}
                                onChangeAutoComplete={items => {
                                    setValue('companies', items.length ? items : [], {
                                        shouldDirty: true,
                                        shouldValidate: true,
                                    });
                                }}
                                groupMultiProps={{
                                    getOptionLabel: option => option.label,
                                }}
                                error={error}
                                className="companies-autocomplete"
                                withCaret
                            />
                        )}
                    />
                )}
                <Controller
                    control={control}
                    name="labels"
                    render={({ field, ref }) => (
                        <div style={{ gridColumn: isMobile ? '1 / span 1' : '1 / span 2' }}>
                            <MultiSelectLabels
                                {...field}
                                ref={ref}
                                onChange={value => {
                                    setValue('labels', value, { shouldDirty: true });
                                }}
                                options={labelsOptions}
                                label={t('common.labels')}
                                fullWidth
                                type={LabelType.Contact}
                                onUpdateCallback={onUpdateLabels}
                                onDeleteCallback={onDeleteLabel}
                                onCreateCallback={newLabel => {
                                    const values = getValues();
                                    setValue('labels', [...values.labels, newLabel], {
                                        shouldDirty: true,
                                    });
                                }}
                            />
                        </div>
                    )}
                />
                {modal.type === ModalType.Edit ? (
                    <>
                        <CheckboxWrapper>
                            <Controller
                                control={control}
                                name="unsubscribed"
                                render={({ field, ref }) => (
                                    <Checkbox
                                        ref={ref}
                                        inputProps={{ 'aria-label': 'controlled' }}
                                        onChange={e =>
                                            setValue('unsubscribed', e.target.checked, { shouldDirty: true })
                                        }
                                        checked={field.value}
                                    />
                                )}
                            />
                            <Typography variant="body2">{t('ContactsPage.unsubscribe')}</Typography>
                        </CheckboxWrapper>
                        <ActivityWrapper>
                            {!isUnsetDate(contact?.createdOn) ? (
                                <div>
                                    <Typography variant="caption" color="textSecondary" sx={{ marginBottom: '6px' }}>
                                        {t('common.createdOn')}
                                    </Typography>
                                    <Typography variant="body2">
                                        {formatDateOrEmpty(contact?.createdOn, 'dd MMM yyyy')}
                                    </Typography>
                                </div>
                            ) : null}
                            {!isUnsetDate(contact?.lastActiveOn) ? (
                                <div>
                                    <Typography variant="caption" color="textSecondary" sx={{ marginBottom: '6px' }}>
                                        {t('ContactsPage.lastActiveOn')}
                                    </Typography>
                                    <Typography variant="body2">
                                        {formatDateOrEmpty(contact?.lastActiveOn, 'dd MMM yyyy, HH:mm')}
                                    </Typography>
                                </div>
                            ) : null}
                        </ActivityWrapper>
                    </>
                ) : null}
            </Content>
        </Modal>
    );
};

export default EditContactModal;
